import React, { useState } from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';

import DialogRoundedModal from '../../../../../components/DialogRoundedModal';
import { CancelButton } from '../../../../../components/Button/ActionButtons';

import {
  ModalContent,
  Section,
  SectionHeading,
  ToggleHeading,
  WorkoutList,
  WorkoutRow,
  WorkoutName,
  WorkoutReason,
} from './styles';
import texts from './texts.json';

const OutcomeSection = ({
  heading,
  workouts,
  withReason,
  tone,
}) => {
  if (workouts.length === 0) {
    return null;
  }

  return (
    <Section>
      <SectionHeading $tone={tone}>{format(heading, { count: workouts.length })}</SectionHeading>
      <WorkoutList>
        {workouts.map(({ id, name, reason }) => (
          <WorkoutRow key={id}>
            <WorkoutName>{name}</WorkoutName>
            {!!withReason && <WorkoutReason>{texts.reason[reason] || texts.reason.UNKNOWN}</WorkoutReason>}
          </WorkoutRow>
        ))}
      </WorkoutList>
    </Section>
  );
};

OutcomeSection.propTypes = {
  heading: PropTypes.string.isRequired,
  workouts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    reason: PropTypes.string,
  })).isRequired,
  withReason: PropTypes.bool,
  tone: PropTypes.oneOf(['failure', 'warning', 'neutral']),
};

OutcomeSection.defaultProps = {
  withReason: false,
  tone: 'neutral',
};

const MigrationResultModal = ({ summary, onClose }) => {
  const [isMigratedListOpen, setIsMigratedListOpen] = useState(false);

  /*
    A run settled from its document counts what copied instead of naming it — the document
    keeps only the failures — so the count cannot be taken from the list's length.
  */
  const { migrated, failed, migratedCount = migrated.length } = summary;

  return (
    <DialogRoundedModal
      open
      onClose={onClose}
      title={texts.title}
      description={format(texts.counts, { migrated: migratedCount, failed: failed.length })}
      fullWidth
      maxWidth="sm"
      actionButtons={<CancelButton onClick={onClose}>{texts.close}</CancelButton>}
    >
      <ModalContent>
        <OutcomeSection
          heading={texts.failedHeading}
          workouts={failed}
          withReason
          tone="failure"
        />
        {migrated.length > 0 && (
          <Section>
            {/*
              A whole-library run can migrate thousands of workouts, so the names are
              mounted only once someone asks for them.
            */}
            <ToggleHeading
              type="button"
              onClick={() => setIsMigratedListOpen((isOpen) => !isOpen)}
              aria-expanded={isMigratedListOpen}
              $isOpen={isMigratedListOpen}
            >
              {format(texts.migratedHeading, { count: migrated.length })}
            </ToggleHeading>
            {isMigratedListOpen && (
              <WorkoutList>
                {migrated.map(({ id, name, suffixApplied }) => (
                  <WorkoutRow key={id}>
                    <WorkoutName>{name}</WorkoutName>
                    {/*
                      Named so the operator can find it: a suffixed copy is listed under a
                      name they never selected, which reads as a missing workout otherwise.
                    */}
                    {!!suffixApplied && <WorkoutReason>{texts.renamed}</WorkoutReason>}
                  </WorkoutRow>
                ))}
              </WorkoutList>
            )}
          </Section>
        )}
      </ModalContent>
    </DialogRoundedModal>
  );
};

MigrationResultModal.propTypes = {
  summary: PropTypes.shape({
    migrated: PropTypes.array,
    migratedCount: PropTypes.number,
    failed: PropTypes.array,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MigrationResultModal;
